import { GridList, GridListItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';
import SimpleBar from 'simplebar-react';

import ArticlePhoto from '@/components/ArticlePhoto';
import AlertBar from '@/design_system/AlertBar';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCross from '@/icons/Cross.svg';
import IconLightBulb from '@/icons/LightBulb.svg';
import IconWarning from '@/icons/Warning.svg';
import { getArticlePhoto, useArticleName } from '@/models/article';
import { ArticleNameCell } from '@/routes/Shipments/Shipment/components/ShipmentForm/components/ArticleSelect/components/ArticleNameCell';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import { ShipmentFormArticle } from '../../ArticleSelect';

import './AddedArticlesSection.css';

const { block, element } = createBEMClasses('added-articles-section');

export const AddedArticlesSection = ({
  addedArticles,
  unaddedArticles,
  setAddedArticles,
  setRemovedArticles,
}: {
  addedArticles: ShipmentFormArticle[];
  unaddedArticles: ShipmentFormArticle[];
  setAddedArticles: (articles: ShipmentFormArticle[]) => void;
  setRemovedArticles: (article: ShipmentFormArticle) => void;
}) => {
  const { t } = useLingui();
  const { isMobile, isTablet } = useViewPort();

  const handleRemoveArticle = (article: ShipmentFormArticle) => {
    setRemovedArticles(article);
    setAddedArticles(addedArticles.filter((a) => a.id !== article.id));
  };

  if (isMobile || isTablet) {
    if (!addedArticles.length) {
      return;
    }

    return (
      <GridList
        className={element('mobile', undefined, 'bg-neutral-100')}
        aria-label={t({
          id: 'shipments.new.articles.added.label',
          message: 'Added',
        })}
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {addedArticles.map((article) => (
          <AddedArticleMobile
            key={article.id}
            article={article}
            onRemove={() => {
              handleRemoveArticle(article);
            }}
          />
        ))}
      </GridList>
    );
  }

  return (
    <Stack className={block(undefined, 'bg-neutral-100')}>
      <Paragraph weight="medium" className={element('label')}>
        <Trans id="shipments.new.articles.added.label">Added</Trans>
      </Paragraph>
      <SimpleBar className={element('scrollbar')} autoHide={false}>
        {!addedArticles.length ? (
          <Paragraph color="secondary" style={{ paddingLeft: '0.5rem' }}>
            <Trans id="shipments.new.articles.added.no-items">
              No items added to the shipment yet.
            </Trans>
          </Paragraph>
        ) : (
          <>
            <GridList
              aria-label={t({
                id: 'shipments.new.articles.added.label',
                message: 'Added',
              })}
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {addedArticles.map((article) => (
                <AddedArticleRow
                  key={article.id}
                  article={article}
                  onRemove={() => {
                    handleRemoveArticle(article);
                  }}
                />
              ))}
            </GridList>
            {addedArticles.length === 1 && unaddedArticles.length > 0 && <AddArticlesAlert />}
          </>
        )}
      </SimpleBar>
    </Stack>
  );
};

const AddedArticleRow = ({
  article,
  onRemove,
}: {
  article: ShipmentFormArticle;
  onRemove: () => void;
}) => {
  const articleName = useArticleName({ article });

  return (
    <GridListItem className={element('added-article-row')} textValue={articleName} id={article.id}>
      <ArticleNameCell
        article={article}
        photoSize="large"
        badge={
          article.quoteRefusedByTheClient ||
          (article.quoteRefusedDuringAnalysis &&
            article.atEntityId !== article.request?.store?.id) ? (
            <Badge size="small" color="warning" borderRadius="full" hasBorder>
              <IconWarning />
              {article?.analysisRefusedAt ? (
                <Trans id="shipments.new.articles.job-refused.badge">Job refused</Trans>
              ) : (
                <Trans id="shipments.new.articles.quote-refused.badge">Quote refused</Trans>
              )}
            </Badge>
          ) : undefined
        }
      />
      <RemoveArticleButton
        className={element('added-article-row__remove-btn')}
        size="medium"
        onRemove={onRemove}
      />
    </GridListItem>
  );
};

const AddedArticleMobile = ({
  article,
  onRemove,
}: {
  article: ShipmentFormArticle;
  onRemove: () => void;
}) => {
  const articleName = useArticleName({ article });

  return (
    <GridListItem
      className={element('mobile__article')}
      textValue={articleName}
      aria-label={articleName}
    >
      <Stack gap="0.5rem" row flexWrap="nowrap">
        <ArticlePhoto photo={getArticlePhoto(article)} size="large" />
        <Stack>
          <Paragraph weight="medium" ellipsis>
            {articleName}
          </Paragraph>
          {article.quoteRefusedByTheClient ||
            (article.quoteRefusedDuringAnalysis &&
              article.atEntityId !== article.request?.store?.id && (
                <Tooltip
                  content={
                    article.analysisRefusedAt ? (
                      <Trans id="shipments.new.articles.job-refused.badge">Job refused</Trans>
                    ) : (
                      <Trans id="shipments.new.articles.quote-refused.badge">Quote refused</Trans>
                    )
                  }
                >
                  <Button variant="style-less">
                    <Badge size="small" color="warning" borderRadius="full" hasBorder iconOnly>
                      <IconWarning />
                    </Badge>
                  </Button>
                </Tooltip>
              ))}
        </Stack>
      </Stack>
      <RemoveArticleButton
        className={element('mobile__article__remove-btn')}
        size="medium"
        onRemove={onRemove}
      />
    </GridListItem>
  );
};

const AddArticlesAlert = () => {
  const { t } = useLingui();

  return (
    <AlertBar
      className={element('add-articles-alert')}
      title={t({
        id: 'shipments.new.articles.added.alert.title',
        message: 'Add items to this shipment',
      })}
      type="info"
      size="large"
      icon={<IconLightBulb />}
    >
      <Trans id="shipments.new.articles.added.alert.text">
        You can add other items with the same destination in order to pool shipments.
      </Trans>
    </AlertBar>
  );
};

const RemoveArticleButton = ({
  className,
  size,
  onRemove,
}: {
  className?: string;
  size: 'small' | 'medium';
  onRemove: () => void;
}) => {
  const { t } = useLingui();

  return (
    <Button
      className={className}
      size={size}
      variant="secondary"
      onPress={onRemove}
      iconOnly
      ariaLabel={t({ id: 'shipments.new.articles.remove', message: 'Remove item' })}
      tooltip={t({ id: 'shipments.new.articles.remove', message: 'Remove item' })}
    >
      <IconCross />
    </Button>
  );
};
